import {
  Button,
  Col,
  Flex,
  Input,
  Modal,
  Row,
  Tabs,
  Select,
  Typography,
  InputNumber,
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import IntlMessage from '../../components/IntlMessage';
import _, { debounce } from 'lodash';
import seatMapApi from '../../api/seat-map';
import operatorsApi from '../../api/operators';
import { useMounted } from '../../hooks/useMounted';

const { Option } = Select;
const { Text } = Typography;

const createInitialSeats = (rows, cols) => {
  const seats = [];
  for (let row = 0; row < rows; row++) {
    const seatRow = [];
    for (let col = 0; col < cols; col++) {
      seatRow.push({ type: 'hidden', number: '' });
    }
    seats.push(seatRow);
  }
  return seats;
};

const SeatMapForm = (props) => {
  const { onClose, currentData, open } = props;

  const { isMounted } = useMounted();
  const [loading, setLoading] = useState(false);
  const intl = useIntl();
  const [title, setTitle] = useState('');
  const [rows, setRows] = useState(0);
  const [cols, setCols] = useState(0);
  const [seats, setSeats] = useState([]);
  const [operators, setOperators] = useState([]);
  const [operatorSelected, setOperatorSelected] = useState(
    currentData ? currentData?.operator?.id : null
  );

  useEffect(() => {
    if (!currentData) {
      setRows(5);
      setCols(10);
      setSeats(createInitialSeats(5, 10));
    }
  }, [currentData]);

  const fetch = useCallback(async () => {
    if (isMounted.current) {
      const filter = {
        page: 1,
        limit: 9999,
        keyword: '',
        order_by: 'title',
        order_dir: 'ASC',
      };
      const res = await operatorsApi.getAll(filter);
      const data = res?.data;
      if (Array.isArray(data)) {
        setOperators(data);
      }
    }
  }, [isMounted]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (currentData) {
      setTitle(currentData.title);

      const arrayFromTwoArrays = [];
      const currentRows = JSON.parse(currentData.block_layout)?.row ?? 0;
      const currentCols = JSON.parse(currentData.block_layout)?.column ?? 0;
      const blockType = JSON.parse(currentData.block_layout)?.block_type ?? [];
      const seatNumber = JSON.parse(currentData.block_layout)?.seatnumber ?? [];

      let index = 0;
      for (let i = 0; i < currentRows; i++) {
        const row = [];
        for (let j = 0; j < currentCols; j++) {
          row.push({
            type: blockType[index],
            number: seatNumber[index],
          });
          index++;
        }
        arrayFromTwoArrays.push(row);
      }
      setRows(currentRows);
      setCols(currentCols);
      setSeats(arrayFromTwoArrays);
    }
  }, [currentData]);

  const handleSubmit = () => {
    // console.log('submit', seats);
    if (
      !title ||
      title.trim() === '' ||
      !cols ||
      Number(cols) < 1 ||
      !rows ||
      Number(rows) < 1 ||
      !operatorSelected
    ) {
      Modal.error({
        title: 'Missing title, number of rows, number of columns or operator',
      });
    } else {
      const blockType = _.flatMap(seats, (row) => row.map((item) => item.type));
      const seatNumber = _.flatMap(seats, (row) =>
        row.map((item) => item.number)
      );
      const blockLayout = {
        row: Number(rows),
        column: Number(cols),
        block_type: blockType,
        seatnumber: seatNumber,
      };
      const payload = {
        title: title.trim(),
        block_layout: JSON.stringify(blockLayout),
        operator_id: operatorSelected,
      };
      // console.log('payload', payload);
      if (currentData) {
        seatMapApi
          .update(currentData.id, payload)
          .then((res) => {
            if (res?.statusCode === 400) {
              Modal.error({
                title: res?.message,
                okText: intl.formatMessage({ id: 'global.close' }),
              });
              setLoading(false);
            } else {
              setLoading(false);
              handleCancel();
              props.onReload();
            }
          })
          .catch((error) => {
            console.error(error);
            setLoading(false);
          });
      } else {
        seatMapApi
          .create(payload)
          .then((res) => {
            if (res?.statusCode === 400) {
              Modal.error({
                title: res?.message,
                okText: intl.formatMessage({ id: 'global.close' }),
              });
              setLoading(false);
            } else {
              setLoading(false);
              handleCancel();
              props.onReload();
            }
          })
          .catch((error) => {
            console.error(error);
            setLoading(false);
          });
      }
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSeatChange = (row, col, field, value) => {
    const newSeats = _.cloneDeep(seats);
    newSeats[row][col][field] = value;
    setSeats(newSeats);
  };

  const handleRowsChange = (value) => {
    setRows(value);
    setSeats(createInitialSeats(value, cols));
  };

  const handleColsChange = (value) => {
    setCols(value);
    setSeats(createInitialSeats(rows, value));
  };

  const handleSearchOperator = debounce(async (value) => {
    try {
      const payload = {
        page: 1,
        limit: value === '' ? 10 : 9999,
        keyword: value || '',
        order_by: 'title',
        order_dir: 'ASC',
      };
      const res = await operatorsApi.getAll(payload);
      let operatorData = res?.data;

      if (Array.isArray(operatorData)) {
        setOperators(operatorData);
      }
    } catch (error) {
      console.error(error);
    }
  }, 300);

  const onChangeOperator = (e) => {
    setOperatorSelected(e);
  };

  const tabItems = [
    {
      key: '1',
      label: <IntlMessage id="global.seat_map" />,
      children: (
        <React.Fragment>
          <Flex vertical gap={16}>
            <Flex gap={8} align="center">
              <Flex gap={4} align="center">
                <Text style={{ wordBreak: 'normal' }}>
                  <IntlMessage id="global.title" />:
                </Text>
                <Input
                  placeholder="Title"
                  style={{ maxWidth: '200px', width: '100%' }}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Flex>
              <Flex gap={4} align="center">
                <Text style={{ wordBreak: 'normal' }}>
                  <IntlMessage id="global.row" />:
                </Text>
                <InputNumber
                  min={1}
                  placeholder="Row number"
                  value={rows}
                  onChange={(value) => handleRowsChange(Number(value))}
                  style={{ maxWidth: '100px', width: '100%' }}
                />
              </Flex>
              <Flex gap={4} align="center">
                <Text style={{ wordBreak: 'normal' }}>
                  <IntlMessage id="global.column" />:
                </Text>
                <InputNumber
                  min={1}
                  placeholder="Column number"
                  value={cols}
                  onChange={(value) => handleColsChange(Number(value))}
                  style={{ maxWidth: '100px', width: '100%' }}
                />
              </Flex>
              <Flex gap={4} align="center">
                <Text style={{ wordBreak: 'normal' }}>
                  <IntlMessage id="global.operator" />:
                </Text>
                <Select
                  showSearch
                  onChange={onChangeOperator}
                  filterOption={false}
                  onSearch={handleSearchOperator}
                  value={operatorSelected}
                  placeholder={intl.formatMessage({
                    id: 'placeholder.select_operator',
                  })}
                >
                  {operators.map((item, index) => {
                    return (
                      <Option value={item.id} key={index}>
                        {item.title}
                      </Option>
                    );
                  })}
                </Select>
              </Flex>
            </Flex>

            <Row gutter={[8, 8]}>
              {seats.map((seatRow, rowIndex) => (
                <Col span={24} key={rowIndex}>
                  <Row gutter={[8, 8]} wrap={false}>
                    {seatRow.map((seat, colIndex) => (
                      <Col key={colIndex}>
                        <div
                          style={{
                            padding: '8px',
                            backgroundColor: '#eee',
                            borderRadius: '5px',
                          }}
                        >
                          <Select
                            value={seat.type}
                            onChange={(value) =>
                              handleSeatChange(
                                rowIndex,
                                colIndex,
                                'type',
                                value
                              )
                            }
                            style={{ width: '100%' }}
                          >
                            <Option value="hidden">Hidden</Option>
                            <Option value="seat">Seat</Option>
                          </Select>
                          <Input
                            placeholder="N"
                            value={seat.number}
                            onChange={(e) =>
                              handleSeatChange(
                                rowIndex,
                                colIndex,
                                'number',
                                e.target.value
                              )
                            }
                            disabled={seat.type === 'hidden'}
                            style={{ marginTop: 8 }}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Col>
              ))}
            </Row>
          </Flex>
        </React.Fragment>
      ),
    },
  ];

  return (
    <Modal
      title={
        currentData ? (
          <IntlMessage id="global.edit" />
        ) : (
          <IntlMessage id="global.add" />
        )
      }
      open={open}
      destroyOnClose={true}
      onCancel={handleCancel}
      width="100%"
      onOk={handleSubmit}
      footer={[
        <Button key="back" type="default" onClick={handleCancel}>
          {<IntlMessage id="global.cancel" />}
        </Button>,
        <Button
          key="submit"
          type="primary"
          htmlType="submit"
          loading={loading}
          onClick={handleSubmit}
        >
          {<IntlMessage id="global.save" />}
        </Button>,
      ]}
    >
      <Tabs defaultActiveKey="1" items={tabItems} />
    </Modal>
  );
};

export default SeatMapForm;
